import { useEffect, useState } from "react"
import { format } from "date-fns"
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Skeleton } from "@/components/ui/skeleton"
import { analyticsService, TimelineEvent, DailyTimelineResponse } from "@/services/analytics"
import { BookOpen, HelpCircle, PenTool, Target, Clock, CheckCircle2, XCircle } from "lucide-react"

interface DailyTimelineModalProps {
    isOpen: boolean
    onClose: () => void
    date: Date | null
}

const getEventIcon = (type: TimelineEvent["type"]) => {
    switch (type) {
        case "PRACTICE": return <PenTool className="w-5 h-5 text-blue-500" />
        case "QUIZ": return <HelpCircle className="w-5 h-5 text-purple-500" />
        case "MOCK_TEST": return <Target className="w-5 h-5 text-red-500" />
        case "READING": return <BookOpen className="w-5 h-5 text-emerald-500" />
    }
}

const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`
    const mins = Math.floor(seconds / 60)
    return `${mins}m`
}

export function DailyTimelineModal({ isOpen, onClose, date }: DailyTimelineModalProps) {
    const [loading, setLoading] = useState(false)
    const [timeline, setTimeline] = useState<DailyTimelineResponse | null>(null)

    useEffect(() => {
        if (!isOpen || !date) return

        const fetchTimeline = async () => {
            setLoading(true)
            try {
                const dateStr = format(date, "yyyy-MM-dd")
                const data = await analyticsService.getDailyTimeline(dateStr)
                setTimeline(data)
            } catch (error) {
                console.error("Failed to fetch daily timeline", error)
            } finally {
                setLoading(false)
            }
        }

        fetchTimeline()
    }, [isOpen, date])

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-2xl w-[95vw] bg-white dark:bg-zinc-950 border border-slate-200 dark:border-zinc-800 p-0 shadow-2xl flex flex-col max-h-[90vh]">
                <DialogHeader className="p-6 pb-4 border-b border-slate-100 dark:border-zinc-800/50 bg-slate-50/50 dark:bg-zinc-900/20">
                    <DialogTitle className="text-xl font-bold text-slate-900 dark:text-zinc-100 flex items-center gap-3">
                        <Clock className="w-5 h-5 text-primary" />
                        Activity Timeline
                    </DialogTitle>
                    <p className="text-sm font-medium text-slate-500 dark:text-zinc-400">
                        {date ? format(date, "EEEE, MMMM do, yyyy") : ""}
                    </p>
                </DialogHeader>

                <div className="p-0 overflow-y-auto flex-1">
                    {loading ? (
                        <div className="p-6 space-y-6">
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="flex gap-4">
                                    <Skeleton className="w-10 h-10 rounded-full shrink-0" />
                                    <div className="space-y-2 flex-1 pt-1">
                                        <Skeleton className="h-5 w-1/2" />
                                        <Skeleton className="h-4 w-3/4" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="p-6">
                            {!timeline?.events.length ? (
                                <div className="text-center py-12 flex flex-col items-center justify-center text-slate-500 dark:text-zinc-500">
                                    <div className="w-16 h-16 bg-slate-100 dark:bg-zinc-900 rounded-full flex items-center justify-center mb-4">
                                        <div className="w-8 h-8 rounded-full border-2 border-dashed border-slate-300 dark:border-zinc-700" />
                                    </div>
                                    <p className="text-base font-semibold text-slate-900 dark:text-zinc-300">No activity recorded</p>
                                    <p className="text-sm mt-1">Take a break or start a new session!</p>
                                </div>
                            ) : (
                                <div className="relative border-l-2 border-slate-100 dark:border-zinc-800/50 ml-4 space-y-8 pb-4">
                                    {timeline.events.map((event, idx) => (
                                        <div key={event.id} className="relative pl-8">
                                            {/* Node */}
                                            <div className="absolute -left-[21px] top-1 w-10 h-10 bg-white dark:bg-zinc-950 border border-slate-200 dark:border-zinc-800 rounded-full flex items-center justify-center shadow-sm">
                                                {getEventIcon(event.type)}
                                            </div>

                                            {/* Content */}
                                            <div className="bg-slate-50 dark:bg-zinc-900/30 rounded-xl p-4 border border-slate-100 dark:border-zinc-800/60 hover:border-slate-300 dark:hover:border-zinc-700 transition-colors">
                                                <div className="flex items-start justify-between gap-4 mb-2">
                                                    <h4 className="font-bold text-slate-900 dark:text-zinc-100 text-lg">{event.title}</h4>
                                                    <span className="text-sm font-semibold text-slate-500 dark:text-zinc-400 uppercase tracking-wide bg-white dark:bg-zinc-900 px-2.5 py-1 rounded-md border border-slate-200 dark:border-zinc-800 shadow-sm">
                                                        {format(new Date(event.timestamp), "h:mm a")}
                                                    </span>
                                                </div>

                                                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                                                    {event.durationSeconds > 0 && (
                                                        <div className="flex items-center gap-1.5 text-slate-500 dark:text-zinc-400 font-medium">
                                                            <Clock className="w-4 h-4" />
                                                            {formatDuration(event.durationSeconds)}
                                                        </div>
                                                    )}

                                                    {event.questionsAnswered > 0 && (
                                                        <div className="flex items-center gap-3">
                                                            <div className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-500 font-medium bg-emerald-50 dark:bg-emerald-500/10 px-2 py-0.5 rounded-md">
                                                                <CheckCircle2 className="w-4 h-4" />
                                                                {event.correctCount} Correct
                                                            </div>
                                                            {event.incorrectCount > 0 && (
                                                                <div className="flex items-center gap-1.5 text-red-600 dark:text-red-400 font-medium bg-red-50 dark:bg-red-500/10 px-2 py-0.5 rounded-md">
                                                                    <XCircle className="w-4 h-4" />
                                                                    {event.incorrectCount} Incorrect
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}

                                                    {event.score !== null && event.score !== undefined && (
                                                        <div className="flex items-center gap-1.5 font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-md">
                                                            <Target className="w-4 h-4" />
                                                            Score: {event.score}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog >
    )
}
